/**
 * Shared API error type + parser.
 *
 * The backend's global Hono error handler (apps/app-manager/api/src/index.ts)
 * returns JSON 500 responses in the shape:
 *   { error: "Internal server error", code: "PostgresError",
 *     message: "permission denied for table org_units", driverCode: "42501" }
 *
 * The fetch helpers in src/api/* should throw `ApiError` so the UI can render
 * the *actual* message and (where relevant) surface actionable hints based on
 * the driver code. Previously each helper independently pulled `error.error`
 * which is always the generic "Internal server error" string — that's why
 * the platform admin had zero visibility into real failures.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly driverCode?: string;

  constructor(init: {
    message: string;
    status: number;
    code: string;
    driverCode?: string;
  }) {
    super(init.message);
    this.name = "ApiError";
    this.status = init.status;
    this.code = init.code;
    this.driverCode = init.driverCode;
  }
}

/**
 * Parse a non-ok Response into an ApiError. Reads the JSON body best-effort
 * and falls back to HTTP status text if the body is missing or unparseable.
 */
export async function parseApiError(response: Response): Promise<ApiError> {
  let body: Record<string, unknown> = {};
  try {
    body = (await response.json()) as Record<string, unknown>;
  } catch {
    // body was not JSON — fall back to status line
  }

  const message =
    (typeof body.message === "string" && body.message) ||
    (typeof body.error === "string" && body.error) ||
    response.statusText ||
    `HTTP ${response.status}`;

  const code =
    (typeof body.code === "string" && body.code) || `HTTP_${response.status}`;

  const driverCode =
    typeof body.driverCode === "string" ? body.driverCode : undefined;

  return new ApiError({ message, status: response.status, code, driverCode });
}
